import {
  birthdayCost,
  debtCollectorCost,
  residenceRentValue,
} from '@kapitalist/contract/ruleset'
import {
  FIVE_M,
  FOUR_M,
  ONE_M,
  TEN_M,
  THREE_M,
  type TStandardPropertyCard,
  TWO_M,
} from '@kapitalist/contract/types'
import {show$} from '@kapitalist/contract/utils/millions/copy'
import {unreachableCase} from '@kapitalist/utils/exhaustive'
import {compact} from '@kapitalist/utils/falsiness'
import {mapRecord} from '@kapitalist/utils/Record'
import type {
  TGenericMinorActionGameLogDescription,
  TSkinGameLogDescriptions,
  TSkinVocab,
} from '~/game/skins/types/copy'
import type {TSkinData, TSpecificCards} from './types'

const neighborhoodNames: Record<TStandardPropertyCard, string> = {
  darkBlue: 'AI',
  brown: 'Gig',
  utility: 'Utility',
  green: 'Cloud Provider',
  yellow: 'Social Network',
  red: 'Streaming Platform',
  orange: 'FinTech',
  pink: 'Rideshare',
  lightBlue: 'E-Commerce',
  railroad: 'Logistics',
}

const shortNeighborhoodNames: Record<TStandardPropertyCard, string> = {
  ...neighborhoodNames,
  green: 'Cloud',
  yellow: 'Social',
  red: 'Streaming',
  lightBlue: 'E-Comm',
}

const vocab: TSkinVocab = {
  neighborhood: {singular: 'vertical', plural: 'verticals'},
  property: {singular: 'company', plural: 'companies'},
  verbs: {
    rotateDual: 'pivot',
    slyDeal: 'steal',
    forcedDeal: 'trade',
    rent: 'charge',
    nonRentDebt: 'charge',
    passGo: 'fundraise',
  },
}

const genericMinorActionDesc = ({
  actor,
  verb,
  targetPossessive,
  cardName,
}: TGenericMinorActionGameLogDescription): string =>
  compact([actor, verb, targetPossessive, cardName]).join(' ')

/* prettier-ignore */
const gameLogDescriptions: TSkinGameLogDescriptions = {
  majorActions: {
    slyDeal: ({actor, playVerb, cardName, targetPossessive, requestedCardName}) =>
      compact([actor, playVerb, cardName, 'against', targetPossessive, requestedCardName]).join(' '),
    forcedDeal: ({actor, playVerb, cardName, targetPossessive, requestedCardName, actorPossessive, offeredCardName}) =>
      compact([actor, playVerb, cardName, 'against', targetPossessive, requestedCardName, 'for', actorPossessive, offeredCardName]).join(' '),
    debtCollector: ({actor, chargeVerb, targetName, cost, actorPossessive, cardName}) =>
      compact([actor, chargeVerb, targetName, cost, 'via', actorPossessive, cardName]).join(' '),
    universalRent: ({actor, chargeVerb, amount, cardName, color}) =>
      compact([
        actor, chargeVerb,
        // plural card names (e.g. "Taxes & Fees") read wrong with an article
        cardName.endsWith('s') ? undefined : 'a',
        'universal', cardName, 'of', amount, 'via', color
      ]).join(' '),
    directionalRent: ({actor, playVerb, amount, shortfall, cardName, color, targetName}) =>
      compact([actor, playVerb, amount, shortfall, cardName, 'via', color, 'against', targetName]).join(' '),
    passGo: ({actor, playVerb, cardName}) =>
      compact([actor, playVerb, cardName]).join(' '),
    dealBreaker: ({actor, playVerb, cardName, targetPossessive, neighborhoodColor, neighborhoodIndex}) =>
      compact([actor, playVerb, cardName, 'against', targetPossessive, 'full', neighborhoodColor, vocab.neighborhood.singular, neighborhoodIndex]).join(' '),
    birthday: ({actor, playVerb, cardName}) =>
      compact([actor, playVerb, cardName]).join(' '),
    changeNeighborhood: ({actor, rotateVerb, cardName, destinationColor}) =>
      compact([actor, rotateVerb, cardName, 'to', destinationColor]).join(' '),
    playToNeighborhood: ({actor, placeVerb, cardName, possessive, neighborhoodColor}) =>
      compact([actor, placeVerb, cardName, 'on',
        neighborhoodColor === undefined
          ? 'the table'
          : `${possessive} ${neighborhoodColor} ${vocab.neighborhood.singular}`,
      ]).join(' '),
    playAsCash: ({actor, playVerb, cardName}) =>
      compact([actor, playVerb, cardName, 'as cash']).join(' '),
    residenceToCash: ({actor, moveVerb, cardName, possessive}) =>
      compact([actor, moveVerb, cardName, 'to', possessive, 'cash pile']).join(' '),
    doubleTheRent: ({actor, hitVerb, target, cardName, color, amount}) =>
      compact([actor, hitVerb, target, 'with', cardName, 'via', color, 'for another', amount]).join(' '),
  },

  minorActionPrompts: {
    slyDeal: genericMinorActionDesc,
    forcedDeal: genericMinorActionDesc,
    dealBreaker: genericMinorActionDesc,
    justSayNo: genericMinorActionDesc,
    payPrompt: ({actor, verb, targetName, amount}) =>
      compact([actor, verb, targetName, amount]).join(' '),
  },

  minorActionResponses: {
    acceptSlyDeal: genericMinorActionDesc,
    acceptForcedDeal: genericMinorActionDesc,
    acceptDealBreaker: ({actor, verb, targetPossessive, cardName}) =>
      compact([actor, verb, targetPossessive, cardName]).join(' '),
    acquiesce: genericMinorActionDesc,
    playJustSayNo: ({tense, actor, verb, cardName, targetPossessive}) =>
      tense === 'imperativePrompt'
        ? compact([verb, cardName]).join(' ')
        : compact([actor, verb, cardName, 'against', targetPossessive, 'attack']).join(' '),
    paySomething: ({actor, verb, targetName, amount, paidWithList}) =>
      compact([actor, verb, targetName, amount, paidWithList]).join(' '),
  },
}

const cardNames: Record<TSpecificCards, string> = {
  // AI
  darkBlue1: 'Anthromorphic',
  darkBlue0: 'Deepthink',
  // Cloud
  green2: 'Data Blox',
  green1: 'ComputeX',
  green0: 'Storeit',
  // Social
  yellow2: 'Chatterbox',
  yellow1: 'PicMe',
  yellow0: 'WeShare',
  // Streaming
  red2: 'Show+',
  red1: 'Streamly',
  red0: 'Flixx',
  // Payments / Fintech
  orange2: 'Tap-n-Go',
  orange1: 'PayMe',
  orange0: 'eCash',
  // Ride-share
  pink2: 'iRide',
  pink1: 'Liftoff',
  pink0: 'Scoot',
  // // AdTech
  // lightBlue2: 'Unitrack',
  // lightBlue1: 'PromoteX',
  // lightBlue0: 'Adverse Media',
  // E-Commerce
  lightBlue2: 'Amazin.com',
  lightBlue1: 'AsiaExpress',
  lightBlue0: 'Shopit',
  // Gig Economy
  brown1: 'Deliveree',
  brown0: 'GigaEats',
  // Logistics
  railroad3: 'HIGHSEAS Shipping Company',
  railroad2: 'URV Delivery',
  railroad1: 'Supreme Air',
  railroad0: 'Last Mile, Incorporated',
  // New-age utilities
  utility1: 'Satellink',
  utility0: 'NorthWest Solar & Wind',

  // Dual Wilds
  wildDarkBlueAndGreen: 'AI / Cloud Startup',
  wildGreenAndRailroad: 'Cloud/Infra Startup',
  wildUtilityAndRailroad: 'Utility/Logistics Startup',
  wildLightBlueAndRailroad: 'E-Comm/Logistics Startup',
  wildLightBlueAndBrown: 'E-Comm/Gig Startup',
  wildPinkAndOrange: 'Rideshare/Payments Startup',
  wildRedAndYellow: 'Short-form Video Startup',

  // Rents
  directionalRent0: 'Hit Piece',
  directionalRent1: 'Patent Suit',
  directionalRent2: 'Cyberattack',
  rentDarkBlueGreen: 'Demand Surcharge', // AI / Cloud
  rentLightBlueBrown: 'Platform Fee', // E-Commerce / Gig
  rentPinkOrange: 'Mandatory Tip', // Ride-share / Payments
  rentUtilityRailroad: 'Taxes & Fees', // Utilities / Logistics
  rentRedYellow: 'Subscription Fee', // Streaming / Social

  // Actions
  dealBreaker: 'Hostile Takeover',
  debtCollector: 'Class-Action Settlement',
  doubleTheRent: 'Surge Pricing',
  slyDeal: 'Involuntary Divestment',
  forcedDeal: 'Bad Deal',
  birthday: 'Corporate Activity Tax',
  justSayNo: 'Cease and Desist',
  passGo: 'Funding Round',

  // Residences
  house: 'Office Tower',
  hotel: 'Corporate Campus',

  // Cash
  tenMil: show$(TEN_M),
  fiveMil: show$(FIVE_M),
  fourMil: show$(FOUR_M),
  threeMil: show$(THREE_M),
  twoMil: show$(TWO_M),
  oneMil: show$(ONE_M),

  // PWC
  propertyWildCard0: 'Stealth Startup',
  propertyWildCard1: 'Stealth Startup',
}

const shortCardDescriptions = (specificCardType: TSpecificCards): string => {
  switch (specificCardType) {
    case 'darkBlue1':
    case 'darkBlue0':
    case 'green2':
    case 'green1':
    case 'green0':
    case 'yellow2':
    case 'yellow1':
    case 'yellow0':
    case 'red2':
    case 'red1':
    case 'red0':
    case 'orange2':
    case 'orange1':
    case 'orange0':
    case 'pink2':
    case 'pink1':
    case 'pink0':
    case 'lightBlue2':
    case 'lightBlue1':
    case 'lightBlue0':
    case 'brown1':
    case 'brown0':
    case 'railroad3':
    case 'railroad2':
    case 'railroad1':
    case 'railroad0':
    case 'utility1':
    case 'utility0':
      return `Companies help you generate profit, and eventually, win the game.`

    // Dual Wilds
    case 'wildDarkBlueAndGreen':
    case 'wildGreenAndRailroad':
    case 'wildUtilityAndRailroad':
    case 'wildLightBlueAndRailroad':
    case 'wildLightBlueAndBrown':
    case 'wildPinkAndOrange':
    case 'wildRedAndYellow':
      return `This startup can pivot to one of two company types.`

    // Rents
    case 'directionalRent0':
    case 'directionalRent1':
    case 'directionalRent2':
      return `Collect profits from any vertical from one other player.`
    case 'rentDarkBlueGreen':
    case 'rentLightBlueBrown':
    case 'rentPinkOrange':
    case 'rentUtilityRailroad':
    case 'rentRedYellow':
      return `Collect profits from a matching vertical from every other player.`

    // Actions
    case 'dealBreaker':
      return `Steal a complete vertical from another player.`
    case 'debtCollector':
      return `Charge one other player ${show$(debtCollectorCost)}.`
    case 'doubleTheRent':
      return `Double your just-collected profits.`
    case 'slyDeal':
      return `Take a company from another player’s incomplete vertical.`
    case 'forcedDeal':
      return `Take a company from another player’s incomplete vertical, but give one of yours in return.`
    case 'birthday':
      return `Charge every other player ${show$(birthdayCost)}.`
    case 'justSayNo':
      return `Refuse to comply with another player’s action played against you (stackable).`
    case 'passGo':
      return `Draw two more cards from the deck.`

    // Residences
    case 'house':
    case 'hotel':
      return `Add ${show$(
        residenceRentValue(specificCardType)
      )} to your complete vertical’s profit margins.`

    // Cash
    case 'tenMil':
    case 'fiveMil':
    case 'fourMil':
    case 'threeMil':
    case 'twoMil':
    case 'oneMil':
      return `Cash helps you pay other players without giving up your corporate assets.`

    // PWC
    case 'propertyWildCard0':
    case 'propertyWildCard1':
      return `This stealth startup can pivot to any existing, incomplete vertical.`
    default:
      return unreachableCase(specificCardType)
  }
}

const shortenCardName = (cardName: string): string =>
  cardName
    .replace('Railroad', 'RR')
    .replace('Place', 'Pl.')
    .replace('Avenue', 'Ave.')
    .replace('Company', 'Co.')
    .replace('Incorporated', 'Inc.')
    .replace('Wildcard', 'Wild')
    .replace('Pennsylvania', 'Penn.')
    .replace(/Northwest/i, 'NW')
    .replace(/((N)orth|(S)outh|(E)ast|(W)est)/, '$2$3$4$5.')

const hexCodesForColors: Record<TStandardPropertyCard, string> = {
  darkBlue: '#1f51b2',
  green: '#007030', // "Oregon green"
  orange: '#EE7709',
  pink: '#DE3891',
  lightBlue: '#A7D1F3',
  red: '#990000', // "USC red"
  yellow: '#FFCC00', // "USC gold"
  utility: '#EFD0A2', // "platinum gold"
  railroad: '#6C3910', // shipping brown
  brown: '#49416B', // purple
}

export const bigTechSkin: TSkinData = {
  cardNames,
  shortCardNames: mapRecord(shortenCardName, cardNames),
  neighborhoodNames,
  shortNeighborhoodNames,
  shortCardDescriptions,
  vocab,
  gameLogDescriptions,
  hexCodesForColors,
  cash: 'identical',
  propertyWildCard: 'individual',
  directionalRent: 'individual',
  ext: 'svg',
  backOfCardFilename: 'back3.svg',
  cardsHaveSharpCorners: false,
}
